import { useState } from 'react';
import { Bell, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/api/client';
import { useRegisterPushSubscription } from '@/api/hooks/usePushSubscriptions';
import {
  isPushConfigured,
  getPermissionState,
  enableDesktopPush,
} from '@/lib/push-notifications';

/** Per-device dismissal — push itself is per-device, so the nag should be too. */
const DISMISSED_KEY = 'wms-push-banner-dismissed';

/**
 * One-click desktop-alerts opt-in bar, shown above the header until this
 * browser is set up (or the user says no).
 *
 * Renders ONLY while enabling is both possible and pending a decision:
 *  - push must be configured for this env and supported by the browser;
 *  - permission must be 'default' — 'granted' devices are auto-registered on
 *    every load by usePushTokenSync (nothing left to ask), and 'denied' can't
 *    be re-prompted (nagging would only frustrate);
 *  - not previously dismissed on this device.
 *
 * The button runs the full setup inline — permission prompt, FCM token mint,
 * backend registration — the same flow as the Alert Preferences card, without
 * leaving the page.
 */
export function EnablePushBanner() {
  const register = useRegisterPushSubscription();
  const [isEnabling, setIsEnabling] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISSED_KEY) === '1',
  );
  const [permission, setPermission] = useState<NotificationPermission>(() =>
    isPushConfigured() ? getPermissionState() : 'denied',
  );

  if (!isPushConfigured() || permission !== 'default' || dismissed) {
    return null;
  }

  const handleEnable = async () => {
    setIsEnabling(true);
    try {
      const { token } = await enableDesktopPush();
      await register.mutateAsync(token);
      toast.success(
        'Desktop alerts are on — WMS can now notify you on this computer.',
      );
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      // Re-read: 'granted' (success) and 'denied' (user refused the browser
      // prompt) both hide the banner on their own merits.
      setPermission(getPermissionState());
      setIsEnabling(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, '1');
    setDismissed(true);
  };

  return (
    <div
      role="status"
      data-testid="enable-push-banner"
      className="flex items-center justify-center gap-3 bg-primary px-4 py-2 text-sm text-primary-foreground"
    >
      <Bell className="h-4 w-4 shrink-0" aria-hidden />
      <span>
        Get WMS alerts as desktop notifications — even when this tab isn&apos;t
        focused.
      </span>
      <button
        type="button"
        onClick={handleEnable}
        disabled={isEnabling}
        data-testid="enable-push-banner-enable"
        className="inline-flex items-center gap-1.5 rounded-md bg-primary-foreground/15 px-3 py-1 font-medium transition-colors hover:bg-primary-foreground/25 disabled:opacity-60"
      >
        {isEnabling && (
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        )}
        Turn on
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss desktop alerts prompt"
        data-testid="enable-push-banner-dismiss"
        className="ml-1 rounded-md p-1 transition-colors hover:bg-primary-foreground/15"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}
